import { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button, Checkbox, message } from "antd";
import { useAgreeGate } from "../components/legal/UseAgreeGate";
import { LOGIN_LEGAL_AGREE_PROMPT } from "../components/legal/LegalAgreePromptContent";
import { GlobalApi } from "../global/GlobalApi";
import { useH5Mock } from "../global/GlobalMock";
import { globalModel } from "../global/GlobalModel";
import { API_DOMAIN } from "../utils/Backend";
import { openLoginCaptcha } from "../utils/tianaiCaptcha";
import { loginPageLayoutCssVars } from "./Config";
import { DigitSplitInput } from "./DigitSplitInput";
import { LoginLegalAgreeText } from "./LoginLegalAgreeText";
import { H5BackButton } from "../components/common/H5BackButton";
import "./LoginStyle.css";

const CODE_SECONDS = 60;
const PHONE_LENGTH = 11;
const CODE_LENGTH = 6;
function sanitizeRedirect(raw: string | null): string {
  const v = raw?.trim() ?? "";
  if (!v.startsWith("/") || v.startsWith("//")) {
    return "/";
  }
  return v;
}

const LoginPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirect = sanitizeRedirect(searchParams.get("redirect"));

  const [phone, setPhone] = useState("");
  const [code, setCode] = useState("");
  const [sending, setSending] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const [agreed, setAgreed] = useState(false);
  const { runWithAgree, agreePromptModal } = useAgreeGate({
    agreed,
    setAgreed,
    uncheckedTip: LOGIN_LEGAL_AGREE_PROMPT.uncheckedTip,
  });

  useEffect(() => {
    if (cooldown <= 0) {
      return;
    }
    const t = window.setInterval(() => {
      setCooldown((c) => (c <= 1 ? 0 : c - 1));
    }, 1000);
    return () => window.clearInterval(t);
  }, [cooldown]);

  useEffect(() => {
    if (globalModel.isLoggedIn) {
      navigate(redirect, { replace: true });
    }
  }, [navigate, redirect]);

  const doSendCode = useCallback(
    async (captchaToken?: string) => {
      setSending(true);
      try {
        const ok = await GlobalApi.sendLoginVerificationCode(phone, captchaToken);
        if (!ok) {
          return;
        }
        message.success("验证码已发送");
        setCode("");
        setCooldown(CODE_SECONDS);
      } finally {
        setSending(false);
      }
    },
    [phone],
  );

  const proceedSendCode = useCallback(async () => {
    if (!/^\d{11}$/.test(phone)) {
      message.warning("请输入11位手机号");
      return;
    }
    if (cooldown > 0 || sending) {
      return;
    }
    if (useH5Mock) {
      await doSendCode();
      return;
    }
    const base = API_DOMAIN.replace(/\/+$/, "");
    try {
      await openLoginCaptcha({
        genUrl: `${base}/lark/app/genCaptcha`,
        checkUrl: `${base}/lark/app/check`,
        onSuccess: (token) => doSendCode(token),
        onFail: () => message.error("滑动验证失败，请重试"),
      });
    } catch {
      message.error("验证码组件加载失败");
    }
  }, [phone, cooldown, sending, doSendCode]);

  const onSendCodeClick = useCallback(() => {
    runWithAgree(() => void proceedSendCode(), LOGIN_LEGAL_AGREE_PROMPT);
  }, [runWithAgree, proceedSendCode]);

  const onSubmit = useCallback(async () => {
    if (!/^\d{11}$/.test(phone)) {
      message.warning("请输入11位手机号");
      return;
    }
    if (!/^\d{4,6}$/.test(code.trim())) {
      message.warning("请输入短信验证码");
      return;
    }
    setSubmitting(true);
    try {
      const user = await GlobalApi.loginByPhoneCode(phone, code.trim());
      if (!user) {
        message.error("验证码错误，请重试");
        return;
      }
      globalModel.setLoggedIn(true, user);
      message.success("登录成功");
      navigate(redirect, { replace: true });
    } finally {
      setSubmitting(false);
    }
  }, [phone, code, navigate, redirect]);

  const onSubmitClick = useCallback(() => {
    runWithAgree(() => void onSubmit(), LOGIN_LEGAL_AGREE_PROMPT);
  }, [runWithAgree, onSubmit]);

  return (
    <>
      {agreePromptModal}
      <div className="login-page-root" style={loginPageLayoutCssVars()}>
        <H5BackButton
          className="login-page-back"
          iconClassName="h5-back-button__icon--invert"
          onClick={() => navigate(-1)}
        />

        <div className="login-page-body">
          <h1 className="login-page-title">手机号登录/注册</h1>

          <div className="login-page-fields">
            <DigitSplitInput
              className="login-page-digit-input--phone"
              length={PHONE_LENGTH}
              value={phone}
              onChange={setPhone}
              prefix="+86"
              placeholder="手机号"
              autoComplete="tel"
              autoFocus
              ariaLabel="手机号"
            />

            <div className="login-page-code-row">
              <DigitSplitInput
                className="login-page-digit-input--code"
                length={CODE_LENGTH}
                value={code}
                onChange={setCode}
                placeholder="短信验证码"
                autoComplete="one-time-code"
                ariaLabel="短信验证码"
              />
              <Button
                className={`login-page-btn-secondary${!agreed ? " login-page-btn-secondary--need-agree" : ""}`}
                disabled={cooldown > 0}
                loading={sending}
                onClick={onSendCodeClick}
              >
                {cooldown > 0 ? `${cooldown}s` : "获取验证码"}
              </Button>
            </div>

            <p className="login-page-hint">首次登录即注册</p>

            <Checkbox
              className="login-page-agree"
              checked={agreed}
              onChange={(e) => setAgreed(e.target.checked)}
            >
              <LoginLegalAgreeText />
            </Checkbox>
          </div>

          <div className="login-page-actions">
            <Button
              type="primary"
              className={`login-page-btn-ok${!agreed ? " login-page-btn-ok--need-agree" : ""}`}
              block
              loading={submitting}
              onClick={onSubmitClick}
            >
              登录
            </Button>
          </div>
        </div>
      </div>
    </>
  );
};

export default LoginPage;
